import React, { useEffect, useMemo, useState } from 'react';
import Breadcrumbs from '../../components/BreadCrumbs';
import Card from '../../components/cards/Card';
import Table from '../../components/tables/Table';
import CustomLoading from '../../components/customLoading/CustomLoading';
import { buildTrackUserOrdersQuery, getTrackUserOrders } from '../../services/orders';
import { toast } from 'react-toastify';
import { BiCopy } from 'react-icons/bi';
import ShowModal from '../../utils/ShowModal';

import OrderOrdersDetailsPopView from './OrderOrdersDetailsPopView';
import { GoEye } from 'react-icons/go';
import AsyncDropdown from '../../components/inputFields/AsyncDropDown';
import { searchUser } from '../../services/search';
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react';

function UserOrderHistory({ user_id, mobile_number, showTopItems = true }) {
    const [modalIsOrderDetailsOpen, setIsOrderDetailsOpen] = useState(false);
    const [openingItem, setOpeningItem] = useState({});

    const [cardData, setCardData] = useState([]);

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({});
    const [userOptions, setUserOptions] = useState([]);


    const headers = [
        { label: "Date", key: "created_at" },
        { label: "Ord. St.", key: "status" },
        { label: "Platform", key: "platform" },
        { label: "Order Value", key: "price" },
        { label: "Pay. St.", key: "payment_status" },
        { label: "Due Payment", key: "remaining_payment" },
        { label: "View", key: "view" },
    ];

    const [filterUserId, setFilterUserId] = useState("");

    const handleUserChange = (value) => {
        setFilterUserId(value);
        fetchData(`?user_id=${value}`);
    };

    const openDetailsModal = (item) => {
        setOpeningItem(item);
        setIsOrderDetailsOpen(true);
    };

    // Close modal
    const closeDetailsModal = () => {
        setIsOrderDetailsOpen(false);
        setOpeningItem({});
    };

    const fetchData = async (query) => {
        setLoading(true);
        try {
            let res = await getTrackUserOrders(query);
            if (res.status === 200) {
                const payload = res.data || {};
                const records =
                    payload.records ??
                    payload.results?.records ??
                    (Array.isArray(payload.results) ? payload.results : []);
                const stats = payload.stats ?? payload.results?.stats ?? [];
                setData({ ...payload, records, stats });
                setCardData(stats);
            } else {
                toast.error("Unable to fetch Data");
            }
            setLoading(false);
        } catch (error) {
            console.error("Error in Orders:", error);
        } finally {
            setLoading(false);
        }
    };

    const copyClipboard = (text) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                toast.success('Copied to clipboard!');
            })
            .catch(() => {
                toast.error('Failed to copy address');
            });
    };

    const trackOrdersQuery = useMemo(
        () => buildTrackUserOrdersQuery({ userId: user_id, mobileNumber: mobile_number }),
        [user_id, mobile_number]
    );

    useEffect(() => {
        if (trackOrdersQuery) {
            fetchData(trackOrdersQuery);
        } else {
            setData({});
            setCardData([]);
            setLoading(false);
        }
    }, [trackOrdersQuery]);

    return (<>
        {
            showTopItems &&
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                { name: 'User Order History' },
            ]} />
        }
        <div className="gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-2">
                {cardData.map((card, index) => (
                    <div
                        key={index}
                        className="bg-white p-4 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors duration-200"
                    >
                        <div className="flex flex-col">
                            <h3 className="text-xs font-medium text-gray-500 mb-1">
                                {card.title}
                            </h3>
                            <p className="text-md font-semibold text-gray-800 line-clamp-2">
                                {card.value}
                            </p>
                            {/* Optional: Add trend indicator */}
                            {card.trend && (
                                <div className={`mt-1 text-xs flex items-center ${card.trend.startsWith('+') ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                    {card.trend.startsWith('+') ? (
                                        <ArrowUpIcon className="w-3 h-3 mr-1" />
                                    ) : (
                                        <ArrowDownIcon className="w-3 h-3 mr-1" />
                                    )}
                                    {card.trend}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
        {showTopItems &&
            <div className="flex flex-row justify-between item-center">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 justify-center items-center max-w-4xl">
                    <AsyncDropdown
                        placeholder="Search User/ Shop/ Phone"
                        apiCallFunction={async (input) => {
                            const response = await searchUser(input);
                            setUserOptions(response.data);
                            return response.data.map((patient) => ({
                                value: patient.id,
                                label: `${patient.shop_name || ""} (${patient.name}) | ${patient.phone_number} ${patient.address ? "|" + patient.address.state : ""}`,
                            }));
                        }}
                        needSearch={true}
                        onChange={(selectedOption) => {
                            // Find the corresponding patient data                                            
                            const selectedPatient = userOptions.find(
                                (item) => item.id === selectedOption.value
                            );
                            if (selectedPatient) {
                                handleUserChange(selectedPatient.id);
                            }
                        }}
                    />
                </div>
            </div>
        }
        {
            loading ?
                <div className="flex w-full flex-row justify-center my-2">
                    <CustomLoading size={100} />
                </div>
                : !trackOrdersQuery ? (
                    <p className="text-xs text-gray-500 text-center py-4">No customer ID or phone to load orders.</p>
                ) : !data?.records?.length ? (
                    <p className="text-xs text-gray-500 text-center py-4">No orders found for this customer.</p>
                ) : (
                <div className="mt-2">
                    <Table
                        headers={headers}
                        rows={data.records.map((item) => (
                            {
                                created_at: <p>{item.order_date}</p>,
                                payment_status: <p className={`${item.payment_status == "COMPLETED" ? "bg-primary" : item.payment_status == "PENDING" ? "bg-red-600" : "bg-yellow-300"} px-2 py-1 rounded-full text-center text-white font-bold text-[0.5rem]`} > {item.payment_status === "PARTIAL PAYMENT" ? "PART" : item.payment_status}</p>,
                                remaining_payment: <p>₹{item.remaining_payment}</p>,
                                status: <p className={`${item.status == "COMPLETED" ? "bg-primary" : item.status == "PENDING" ? "bg-red-600" : "bg-yellow-300"} px-2 py-1 rounded-full text-center text-white font-bold text-[0.5rem]`} > {item.status === "PARTIAL PAYMENT" ? "PART" : item.status}</p>,
                                platform: <p>{item.platform}</p>,
                                price: <p>₹{item.total_price}</p>,
                                address: <span className='flex flex-row gap-1 items-center'><p className="max-w-16 truncate cursor-help" title={item.shipping_user?.shipping_address}>{item.shipping_user?.shipping_address}</p> <BiCopy
                                    className='cursor-pointer'
                                    color='blue'
                                    onClick={() => { copyClipboard(item.shipping_user?.shipping_address) }}
                                /></span>,
                                view: <div className="flex flex-row justify-center"><GoEye size={24} color='purple' className='cursor-pointer' onClick={() => { openDetailsModal(item) }} /></div>,
                            }))}
                    />
                </div>)}
        <ShowModal
            contentLabel={"Order Details alert"}
            isOpen={modalIsOrderDetailsOpen}
            modalWidth="50%"
            isSidebar={true}
            onRequestClose={closeDetailsModal}
            componentToPass={
                <OrderOrdersDetailsPopView
                    item={openingItem}
                    order_id={openingItem?.id}
                />
            }
        />
    </>);
}

export default UserOrderHistory;